import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, ChevronDown, User, BookMarked, LogOut, Bell, Wrench } from 'lucide-react';
import { Button } from './ui/button';
import MeridianLogo from './MeridianLogo';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/');
    setUserMenuOpen(false);
  };

  // Close Tools dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) {
        setToolsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const mainNavLinks = [
    { label: 'Find Procedures', to: '/procedures' },
    { label: 'Providers', to: '/providers' },
    { label: 'Compare Costs', to: '/compare' },
  ];

  const toolsLinks = [
    { label: 'Cost Calculator', to: '/total-cost' },
    { label: 'AI Assistant', to: '/ai-assistant' },
  ];

  const isToolsActive = toolsLinks.some(l => location.pathname === l.to);

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-stone-200" style={{ backgroundColor: '#F0EEE9' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <Link to="/" data-testid="nav-logo">
            <MeridianLogo variant="light" />
          </Link>

          {/* Desktop center nav */}
          <div className="hidden md:flex items-center gap-8">
            {mainNavLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-medium transition-colors hover:text-[#0D3D4A] ${
                  location.pathname === link.to ? 'text-[#0D3D4A]' : 'text-stone-600'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Tools dropdown */}
            <div className="relative" ref={toolsRef}>
              <button
                onClick={() => setToolsOpen(prev => !prev)}
                className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-[#0D3D4A] ${
                  isToolsActive ? 'text-[#0D3D4A]' : 'text-stone-600'
                }`}
                data-testid="tools-dropdown-btn"
              >
                Tools
                <ChevronDown size={14} className={`transition-transform ${toolsOpen ? 'rotate-180' : ''}`} />
              </button>

              {toolsOpen && (
                <div
                  className="absolute left-0 top-full mt-2 w-44 shadow-lg z-50 py-1"
                  style={{ backgroundColor: '#0D3D4A' }}
                >
                  {toolsLinks.map(link => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setToolsOpen(false)}
                      className="block px-4 py-2.5 text-sm text-white/90 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Desktop right side */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/provider/register"
              className="text-xs text-stone-500 hover:text-[#0D3D4A] transition-colors font-medium"
              data-testid="list-your-practice-link"
            >
              List Your Practice
            </Link>
            <Link
              to="/for-physicians"
              className="text-xs text-stone-500 hover:text-[#0D3D4A] transition-colors mr-1 font-medium"
              data-testid="for-physicians-link"
            >
              For Physicians
            </Link>
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 text-sm font-medium text-stone-700 hover:text-[#0D3D4A] transition-colors"
                  data-testid="user-menu-button"
                >
                  <div className="w-8 h-8 rounded-full bg-[#0D3D4A]/10 flex items-center justify-center">
                    <span className="text-[#0D3D4A] font-semibold text-xs">{user.full_name?.[0]?.toUpperCase()}</span>
                  </div>
                  <span className="hidden lg:block">{user.full_name?.split(' ')[0]}</span>
                  <ChevronDown size={14} />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-stone-200 shadow-lg py-1 z-50">
                    <Link to="/dashboard" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-stone-700 hover:bg-[#F0EEE9]">
                      <BookMarked size={14} /> Saved Searches
                    </Link>
                    <Link to="/profile" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-stone-700 hover:bg-[#F0EEE9]">
                      <User size={14} /> Profile
                    </Link>
                    <button onClick={handleLogout} className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-[#F0EEE9]" data-testid="logout-button">
                      <LogOut size={14} /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate('/auth')} data-testid="nav-login-btn" className="text-stone-700">
                  Sign In
                </Button>
                <button
                  onClick={() => navigate('/auth?mode=register')}
                  className="bg-[#0D3D4A] text-white px-5 py-2 text-sm font-medium hover:bg-[#0E4557] transition-colors"
                  data-testid="nav-register-btn"
                >
                  Get Started
                </button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button className="md:hidden p-2" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-button">
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile menu — all items retained */}
        {menuOpen && (
          <div className="md:hidden py-4 border-t border-stone-200">
            {mainNavLinks.map(link => (
              <Link key={link.to} to={link.to} onClick={() => setMenuOpen(false)}
                className="block py-3 text-sm font-medium text-stone-600 hover:text-[#0D3D4A]">
                {link.label}
              </Link>
            ))}
            {toolsLinks.map(link => (
              <Link key={link.to} to={link.to} onClick={() => setMenuOpen(false)}
                className="block py-3 text-sm font-medium text-stone-600 hover:text-[#0D3D4A]">
                {link.label}
              </Link>
            ))}
            <Link to="/my-alerts" onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 py-3 text-sm font-medium text-stone-600 hover:text-[#0D3D4A]">
              <Bell size={14} /> My Alerts
            </Link>
            <Link to="/provider/register" onClick={() => setMenuOpen(false)}
              className="block py-3 text-sm font-medium text-[#0D3D4A] border-t border-stone-200 mt-1 pt-4">
              List Your Practice
            </Link>
            <Link to="/for-physicians" onClick={() => setMenuOpen(false)}
              className="block py-3 text-sm font-medium text-stone-500 hover:text-[#0D3D4A]">
              For Physicians
            </Link>
            <Link to="/provider-portal/login" onClick={() => setMenuOpen(false)}
              className="block py-3 text-sm font-medium text-stone-500 hover:text-[#0D3D4A]">
              Provider Login
            </Link>
            {user ? (
              <button onClick={handleLogout} className="w-full text-left py-3 text-sm font-medium text-red-600">Sign Out</button>
            ) : (
              <div className="flex gap-3 pt-3">
                <Button variant="outline" size="sm" onClick={() => { navigate('/auth'); setMenuOpen(false); }}>Sign In</Button>
                <Button size="sm" onClick={() => { navigate('/auth?mode=register'); setMenuOpen(false); }} className="bg-[#0D3D4A] hover:bg-[#0E4557]">Get Started</Button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
